import {
  useState,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  RefObject,
  useImperativeHandle,
} from "react"

import Tooltip from "@/components/tooltip/Tooltip"
import { ChartRow, ChartScatterData, ToolTipContents } from "@/app/report/common/constructor"
import { ChartStyle } from "@/app/report/common/chart/scatter"
import { Chart } from "@/app/report/common/chart/scatter"
import { getDateTimeString, getMetricValueStr, TypeChecker } from "@/app/report/common/tools"
import { DRAWER_WIDTH, REPORT_PADDING, REPORT_SECTION_PADDING } from "@/app/report/common/constants"
import { createDrawerStore } from "@/stores/global"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { EXPORT_TYPE, ExportFileFunctionType } from "@/app/report/common/type"
import { PDF } from "@/app/report/common/export/pdf"
import { exportSVG } from "@/app/report/common/export/export"

const DEFAULT_CHART_WIDTH = 590
const DEFAULT_CHART_HEIGHT = 400
const SCATTER_CHART_RATIO = 0.8
const SCATTER_ROW_GAP = 28

function ScatterChart(
  {
    chartData,
  }: {
    loading: boolean
    chartData?: ChartScatterDatayAxisTickAndSpace
  },
  ref: React.ForwardedRef<{
    drawChart: () => void
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)

  const [tooltipTitles, setTooltipTitles] = useState<string[]>([])
  const [tooltipValues, setTooltipValues] = useState<Array<ToolTipContents>>([])
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)

  const scatter = useRef<Chart | null>(null)
  const exportScatter = useRef<Chart | null>(null)

  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  const isMounted = useRef(false)

  const translateKey = useTranslateMetricKey()

  const getAxisName = useCallback(
    (name: string | undefined) => {
      if (!name) return ""
      if (name.includes("metric.")) {
        return translateKey(name.split("metric.")[1]) || name
      }
      return name
    },
    [translateKey],
  )

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  // 초기 마운트 시 resize 실행
  useEffect(() => {
    handleResize()
  }, [])

  // drawer 상태 변경 시 resize (데스크톱만)
  useEffect(() => {
    window.addEventListener("resize", handleResize)

    const isMobileScreen = window.innerWidth < 1200
    if (!isMobileScreen) {
      handleResize()
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (chartData) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight])

  const handleResize = () => {
    const containerWidth = document.querySelector(".Container")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth =
        containerWidth -
        drawerOffset -
        REPORT_PADDING -
        REPORT_SECTION_PADDING * 2 -
        SCATTER_ROW_GAP -
        20

      if (isMounted.current) {
        setChartWidth(currentChartWidth / 2)
        setChartHeight((currentChartWidth / 2) * SCATTER_CHART_RATIO)
      }
    }
  }

  const mouseoverAxisXAvg = (_: MouseEvent, value: number, scatterRef: RefObject<Chart | null>) => {
    if (!scatterRef.current || !chartData) {
      return
    }

    scatterRef.current.setAxisAvgXOpacity(1.0)

    const newName = chartData.axisX.name

    const xValueText = getMetricValueStr({
      value,
      decimalPoint: chartData.axisX.decimalPoint,
      calcType: "total",
      unit: chartData.axisX.unit,
      metric: newName,
    })

    const newTooltipTitles = ["Avg."]
    const newTooltipValues = [
      new ToolTipContents({
        title: getAxisName(chartData.axisX.name),
        value: xValueText,
      }),
    ]

    setTooltipTitles(newTooltipTitles)
    setTooltipValues(newTooltipValues)
    setTooltipVisible(true)
  }

  const mouseoverAxisYAvg = (_: MouseEvent, value: number, scatterRef: RefObject<Chart | null>) => {
    if (!scatterRef.current || !chartData) {
      return
    }

    scatterRef.current.setAxisAvgYOpacity(1.0)

    const newName = chartData.axisY.name

    const yValueText = getMetricValueStr({
      value: value,
      decimalPoint: chartData.axisY.decimalPoint,
      calcType: "total",
      unit: chartData.axisY.unit,
      metric: newName,
    })

    const newTooltipTitles = ["Avg."]
    const newTooltipValues = [
      new ToolTipContents({
        title: getAxisName(chartData.axisY.name),
        value: yValueText,
      }),
    ]

    setTooltipTitles(newTooltipTitles)
    setTooltipValues(newTooltipValues)
    setTooltipVisible(true)
  }

  const mouseoutAxisXYAvg = (scatterRef: RefObject<Chart | null>) => {
    if (!scatterRef.current || !chartData) {
      return
    }

    scatterRef.current.setAxisAvgXOpacity(0.5)
    scatterRef.current.setAxisAvgYOpacity(0.5)

    setTooltipVisible(false)
  }

  const mouseover = (e: MouseEvent, row: ChartRow, scatterRef: RefObject<Chart | null>) => {
    if (
      !scatterRef.current ||
      !row ||
      !chartData ||
      !TypeChecker.isNumber(row.data[0]) ||
      !TypeChecker.isNumber(row.data[1])
    ) {
      return
    }

    const unitY = chartData.axisY.unit

    const newNameX = getAxisName(chartData.axisX.name)
    const newNameY = getAxisName(chartData.axisY.name)

    const xValueText = getMetricValueStr({
      value: row.data[0],
      decimalPoint: chartData.axisX.decimalPoint,
      calcType: "total",
      unit: chartData.axisX.unit,
      metric: newNameX,
    })

    const yValueText = getMetricValueStr({
      value: row.data[1],
      decimalPoint: chartData.axisY.decimalPoint,
      calcType: "total",
      unit: unitY,
      metric: newNameY,
    })

    const newTooltipTitles = [`${row.header.get("title")}`]

    const newTooltipValues = new Array<ToolTipContents>()
    newTooltipValues.push(
      new ToolTipContents({
        title: newNameX,
        value: xValueText,
      }),
    )
    newTooltipValues.push(
      new ToolTipContents({
        title: newNameY,
        value: yValueText,
      }),
    )

    setTooltipTitles(newTooltipTitles)
    setTooltipValues(newTooltipValues)
    setTooltipVisible(true)

    scatterRef.current.setHighlightCircle(e, row, true)
  }

  const mousemove = (scatterRef: RefObject<Chart | null>) => {
    if (!scatterRef.current) {
      return
    }
  }

  const mouseout = (e: MouseEvent, row: ChartRow, scatterRef: RefObject<Chart | null>) => {
    if (!scatterRef.current || !e) {
      return
    }

    setTooltipVisible(false)
    scatterRef.current.setHighlightCircle(e, row, false)
  }

  const drawChart = useCallback(async () => {
    if (!elementChartContainer.current || !chartElements.current || !chartData) {
      return
    }

    const chartStyle = new ChartStyle({
      width: chartWidth || DEFAULT_CHART_WIDTH,
      height: chartHeight || DEFAULT_CHART_HEIGHT,
      color: chartData.settings.color,
    })

    scatter.current = new Chart({
      axisX: chartData.axisX,
      axisY: chartData.axisY,
      rows: chartData.data,
      chartStyle,
      mouseoverAxisXAvg: (mouseEvent, value) => mouseoverAxisXAvg(mouseEvent, value, scatter),
      mouseoverAxisYAvg: (mouseEvent, value) => mouseoverAxisYAvg(mouseEvent, value, scatter),
      mouseoutAxisXYAvg: () => mouseoutAxisXYAvg(scatter),
      mouseover: (mouseEvent, row) => mouseover(mouseEvent, row, scatter),
      mousemove: () => mousemove(scatter),
      mouseout: (mouseEvent, row) => mouseout(mouseEvent, row, scatter),
      translateKey,
    })

    if (scatter.current && scatter.current.svg) {
      const node = scatter.current.svg.node()
      if (node) {
        chartElements.current.innerHTML = ""
        chartElements.current.appendChild(node)
      }
    }
  }, [chartData, chartWidth, chartHeight])

  const exportFile = async (args: ExportFileFunctionType) => {
    const { exportType, magnification, reportType } = args
    const FILE_NAME_HEADER = `${reportType}_report_chart_scatter`
    const fileName = `${getDateTimeString()}_${FILE_NAME_HEADER}`
    const pdf = await PDF.create()

    const chartSizeForExport = { width: 800, height: 640 }
    if (!chartData) {
      return
    }

    const chartStyle = new ChartStyle({
      width: chartSizeForExport.width,
      height: chartSizeForExport.height,
      color: chartData.settings.color,
      magnification,
    })

    exportScatter.current = new Chart({
      axisX: chartData.axisX,
      axisY: chartData.axisY,
      rows: chartData.data,
      chartStyle,
      mouseoverAxisXAvg: (mouseEvent, value) => mouseoverAxisXAvg(mouseEvent, value, exportScatter),
      mouseoverAxisYAvg: (mouseEvent, value) => mouseoverAxisYAvg(mouseEvent, value, exportScatter),
      mouseoutAxisXYAvg: () => mouseoutAxisXYAvg(exportScatter),
      mouseover: (mouseEvent, row) => mouseover(mouseEvent, row, exportScatter),
      mousemove: () => mousemove(exportScatter),
      mouseout: (mouseEvent, row) => mouseout(mouseEvent, row, exportScatter),
      translateKey,
    })

    if (exportScatter.current && exportScatter.current.svg) {
      const chart = exportScatter.current.svg.node()
      if (chart) {
        await exportSVG(chart, fileName, "white", exportType, pdf)

        if (exportType === EXPORT_TYPE.PDF) {
          const fileName = `${getDateTimeString()}_${FILE_NAME_HEADER}.pdf`
          pdf.save(fileName)
        }
      }
    }
  }

  useImperativeHandle(ref, () => ({
    drawChart,
    exportFile: async (args: ExportFileFunctionType) => exportFile(args),
  }))

  return (
    <Tooltip
      type="chart"
      label={tooltipTitles[0]}
      title={
        <div className="bar-chart-tooltip">
          {tooltipValues.map((value, index) => {
            if (!value.value || !value.title) {
              return null
            }

            return (
              <div key={index} className="bar-chart-tooltip-item">
                <p>{value.title}:</p>
                <p>{value.value}</p>
              </div>
            )
          })}
        </div>
      }
      size="medium"
      open={tooltipVisible}>
      <div className="chart">
        <div ref={elementChartContainer} className="element-chart-container">
          <div ref={chartElements}></div>
        </div>
      </div>
    </Tooltip>
  )
}

export default forwardRef(ScatterChart)
